<script>
    import { onMount } from 'svelte';
    import { 
        fetchTwoFaConfig, 
        setupTwoFaConfig 
    } from '/public/services/users/users';
    import { fade } from 'svelte/transition';
    
    export let params;

    let hash = params.hash;
    let loading = true;
    let name = null;
    let qr = null;
    let stage = 1;
    let otp = null;

    function setStage(n) {
        stage = n;
    }

    function challengeOTP(e) {
        e.preventDefault();
        setupTwoFaConfig(hash,otp).then(res => {
            if (res.success) {
                window.location = res.data.url;
            }
        });
    }

    onMount(() => {
        fetchTwoFaConfig(hash).then(res => {
            if (res.success) {
                name = res.data.name;
                qr = res.data.qr;

                loading = false;
            }
        });
    });

</script>

<h3 class='mb-3'>
    <span class="fas fa-shield-alt"></span> Two factor authentication setup
</h3>

{#if loading === true}

    <div class="text-center">
        <h4>
            <span class="ajax-ball-loader"></span> Checking your authentication permissions....
        </h4>
    </div>

{:else}
    
    <div class="row justify-content-center">
        <div class="col-sm-12 col-md-8">
            <div class="card">
                <div class="card-header">
                    <span class="fas fa-shield-alt"></span> 2FA setup for {name}
                </div>
                <div class="card-body">
                    <h3 class='mb-3'>
                        Hi { name }
                    </h3>

                    {#if stage == 1}
                        <section in:fade>
                            <h3 class='mb-3'>
                                Step 1: Download a mobile Authenticator app
                            </h3>
                            <p>
                                You need to download an app to your mobile that you will use to generate One Time Passwords (OTP) during the login process.<br/>
                                You will not be able to login and use this app without completing this 2FA setup.
                            </p>
                            <p>
                                We recommend the following, but if you use LastPass or 1Password managers then support for 2FA is available in their respective apps.
                            </p>
                            <div class="row mb-3">
                                <div class="col-md-4">
                                    <div>
                                        <img src="/images/2fa/authy.png" alt="Authy logo" class="img-fluid mx-auto d-block" style="max-height: 180px;"/>
                                    </div>
                                    <hr/>
                                    <div class="text-center">
                                        <h4>
                                            Authy
                                        </h4>
                                        <div>
                                            <a href="https://authy.com/" target="_blank">
                                                https://authy.com/
                                            </a>
                                        </div>
                                        <small>
                                            IOS, Android, MacOS, Windows, Linux
                                        </small>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="">
                                        <img src="/images/2fa/microsoft.png" alt="Microsoft Authenticator logo" class="img-fluid mx-auto d-block" style="max-height: 180px;"/>
                                    </div>
                                    <hr/>
                                    <div class="text-center">
                                        <h4>
                                            Microsoft Authenticator
                                        </h4>
                                        <div>
                                            <a href="https://www.microsoft.com/en-nz/p/authenticator/9wzdncrfj3rj?activetab=pivot:overviewtab" target="_blank">
                                                https://www.microsoft.com/
                                            </a>
                                        </div>
                                        <small>
                                            IOS, Android, Windows
                                        </small>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="">
                                        <img src="/images/2fa/google.png" alt="Google authenticator logo" class="img-fluid mx-auto d-block" style="max-height: 180px;"/>
                                    </div>
                                    <hr/>
                                    <div class="text-center">
                                        <h4>
                                            Google Authenticator
                                        </h4>
                                        <div>
                                            <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US&gl=US" target="_blank">
                                                Android
                                            </a>
                                        </div>
                                        <div>
                                            <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank">
                                                IOS
                                            </a>
                                        </div>
                                        <small>
                                            IOS, Android
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div class="text-center">
                                <hr/>
                                <button type="button" class="btn btn-success" on:click={() => setStage(2)}>
                                    <span class="fas fa-check-circle"></span> I have installed an authenticator app on my mobile device
                                </button>
                            </div>
                        </section>
                    {/if}

                    {#if stage == 2}

                        <section in:fade>
                            <h3>
                                Step 2: Setup the QR code for this app
                            </h3>
                            <p class="text-right">
                                <button type="button" class="btn btn-outline-secondary float-end" on:click={() => setStage(1)}>
                                    <span class="fas fa-chevron-left"></span> Back
                                </button>
                            </p>
                            <p>
                                You need to add the QR code below to your app<br/>
                                You may need to consult the documentation for your Authenticator app to do this.
                            </p>
                            <div class="center-block">
                                <img alt="2FA QR code" src="{ qr }" class="img-fluid d-block mx-auto"/>
                            </div>
                            <hr/>
                            <div class="text-center">
                                <button type="button" class="btn btn-success d-block mx-auto" on:click={() => setStage(3)}>
                                    <span class="fas fas-check-circle"></span> I have successfully added the QR code to my authenticator app
                                </button>
                            </div>
                        </section>

                    {/if}

                    {#if stage == 3}

                        <section in:fade>
                            <h3 class='mb-3'>
                                Step 3: OTP Challenge
                            </h3>
                            <p class="text-right">
                                <button type="button" class="btn btn-outline-secondary float-end" on:click={() => setStage(2)}>
                                    <span class="fas fa-chevron-left"></span> Back
                                </button>
                            </p>
                            <p class="mb-3">
                                We need to confirm your 2FA setup in your authenticator app<br/>
                                Enter the OTP code from your authenticator app in the form below.<br/>
                                A successfully OTP challenge will complete 2FA setup and log you into the app.<br/>
                                Note: you will be asked to complete this challenge on a regular basis to confirm your access to this app.
                            </p>
                            <form class="form" on:submit={challengeOTP}>
                                <div class="row mb-3">
                                    <label class="col-sm-2 col-form-label" for="otp_code">
                                        OTP
                                    </label>
                                    <div class="col-sm-8">
                                        <input 
                                            type="text" 
                                            id="otp_code" 
                                            class="form-control" 
                                            required 
                                            bind:value={otp} 
                                            placeholder="Enter your OTP code"
                                            class:is-invalid='{otp == null}'
                                        />
                                    </div>
                                    <div class="col-sm-2">
                                        <button type="submit" class="btn btn-primary">
                                            <span class="fas fa-save"></span>
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </section>

                    {/if}

                </div>
            </div>
        </div>
    </div>

{/if}
